import { ReactChild, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import {
  COLLAPSE_REVEAL_EVENT,
  ICollapseRevealEventDetail,
  isRevealTargetInsideBlock,
} from 'CorePackage/MainModule/common/revealCollapsedElement';
import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { Spinner } from 'UIPackage/MainModule/components/Spinner';

import styles from './KnmCollapse.module.scss';

interface IKnmCollapseProps {
  id?: string;
  /**
   * Дополнительные id блоков, лежащих внутри аккордеона, когда одного общего
   * префикса у них нет (см. isRevealTargetInsideBlock). Цель, попадающая в любой
   * из них, раскрывает аккордеон так же, как попадание в собственный `id`.
   */
  revealIds?: string[];
  theme?: 'default' | 'fields';
  title: string | ReactElement;
  subtitle?: string | ReactElement;
  children: ReactChild;
  isNew?: boolean;
  isNewTitle?: string;
  openByDefault?: boolean;
  opened?: boolean;
  onClose?: () => void;
  onToggle?: () => void;
  onToggleCallback?: () => void;
  bodyClassName?: string | undefined;
  headerClassName?: string | undefined;
  showWarningTooltip?: boolean;
  tooltipText?: string;
  headerPostfix?: ReactNode;
  showLeftWarningMarker?: boolean;
  additionalButtons?: ReactNode;
  changeOpenedCallback?: (opened: boolean) => void;
  isShowOpenButton?: boolean;
  error?: boolean;
  /** Отключить отложенный рендер с лоадером при раскрытии */
  disableDeferredRender?: boolean;
  withIconChange?: boolean;
}

export const KnmCollapse = ({
  id,
  revealIds,
  theme = 'default',
  title,
  subtitle,
  children,
  isNew,
  isNewTitle,
  openByDefault,
  opened,
  onClose,
  onToggle,
  onToggleCallback,
  bodyClassName,
  showWarningTooltip,
  tooltipText,
  headerPostfix,
  headerClassName,
  showLeftWarningMarker,
  additionalButtons,
  changeOpenedCallback,
  error = false,
  disableDeferredRender = false,
  withIconChange,
  isShowOpenButton = true,
}: IKnmCollapseProps) => {
  const [isRolledUp, setRollUp] = useState(!openByDefault);
  const toggleRollUp = () => setRollUp(prev => !prev);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // не рендерим children пока коллапс ни разу не был открыт
  const hasBeenOpened = useRef(!isRolledUp);

  if (!isRolledUp) {
    hasBeenOpened.current = true;
  }

  // Отложенный рендер: при первом раскрытии показываем спиннер,
  // потом через RAF+setTimeout рендерим children
  const [isContentReady, setIsContentReady] = useState(!isRolledUp);
  const deferTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isRolledUp && !isContentReady && !disableDeferredRender) {
      deferTimerRef.current = window.setTimeout(() => {
        setIsContentReady(true);
        deferTimerRef.current = null;
      }, 60);
    }

    return () => {
      if (deferTimerRef.current) {
        clearTimeout(deferTimerRef.current);
        deferTimerRef.current = null;
      }
    };
  }, [isRolledUp, isContentReady, disableDeferredRender]);

  useEffect(() => {
    changeOpenedCallback?.(isRolledUp);
  }, [isRolledUp]);

  useEffect(() => {
    if (typeof opened === 'boolean') {
      setRollUp(!opened);
    }
  }, [opened]);

  const wrapperAttributes = {
    ...(theme === 'default' && { 'data-default': true }),
    ...(theme === 'fields' && { 'data-fields': true }),
  };

  const openButtonAttributes = {
    ...(!isRolledUp && { 'data-openned': true }),
  };

  const bodyAttributes = {
    ...(!isRolledUp && { 'data-active': true }),
  };

  const onClick = () => {
    onToggleCallback?.();
    if (onToggle) {
      onToggle();
    } else {
      toggleRollUp();
    }
  };

  /**
   * Раскрытие по запросу извне (переход по ссылке из панели ошибок в шапке паспорта).
   * Пока аккордеон свёрнут, поля внутри не отрисованы и найти их в DOM нельзя,
   * поэтому цель определяем по id блока, а уже отрисованные (но скрытые) поля -
   * дополнительно по вхождению в обёртку.
   */
  const revealIdsRef = useRef(revealIds);

  revealIdsRef.current = revealIds;

  const revealRef = useRef<() => boolean>();

  revealRef.current = () => {
    if (!isRolledUp) return false;

    onClick();

    return true;
  };

  useEffect(() => {
    const handleReveal = (event: Event) => {
      const { detail } = event as CustomEvent<ICollapseRevealEventDetail | undefined>;
      const elementId = detail?.elementId;

      if (!detail || !elementId) return;

      const target = document.getElementById(elementId);
      const isInside = !!target && !!wrapperRef.current && wrapperRef.current.contains(target);

      const isInsideOwnBlock =
        isRevealTargetInsideBlock(elementId, id) ||
        !!revealIdsRef.current?.some(revealId => isRevealTargetInsideBlock(elementId, revealId));

      if (!isInsideOwnBlock && !isInside) return;

      // Сообщаем инициатору, что раскрытие продвинулось: пока это происходит,
      // он продолжает опрашивать вложенные уровни
      if (revealRef.current?.()) detail.revealed = true;
    };

    window.addEventListener(COLLAPSE_REVEAL_EVENT, handleReveal);

    return () => window.removeEventListener(COLLAPSE_REVEAL_EVENT, handleReveal);
  }, [id]);

  return (
    <div
      ref={wrapperRef}
      className={`${styles.Wrapper} ${error ? styles.Error : ''} ${headerClassName}`}
      id={id}
      {...wrapperAttributes}
    >
      <div className={styles.Header}>
        {showLeftWarningMarker && <div className={styles.LeftWarningMarker}></div>}
        <div className={styles.HeaderPadding}>
          <div className={styles.HeaderContent}>
            <span className={` ${styles.Title} ${withIconChange ? styles.W100 : ''}`}>{title}</span>
            {isNew && <span className={styles.NewBadge}>{isNewTitle || 'Новый'}</span>}
            {subtitle && <span className={styles.Subtitle}>{subtitle}</span>}
          </div>

          {showWarningTooltip && tooltipText && (
            <div className={styles.Tooltip}>
              <div className={styles.TooltipIcon}>
                <span className={styles.TooltipText}>{tooltipText}</span>
              </div>
            </div>
          )}

          {headerPostfix && <div className={styles.HeaderPostfix}>{headerPostfix}</div>}

          {additionalButtons && <div className={styles.AdditionalButtons}>{additionalButtons}</div>}
          {onClose && (
            <button className={styles.DeleteButton} type="button" onClick={onClose}>
              <span className={styles.DeleteButtonIcon} />
            </button>
          )}
          {isShowOpenButton && (
            <button className={styles.OpenButton} type="button" onClick={onClick} {...openButtonAttributes}>
              <IconCollapse className={styles.OpenButtonIcon} />
              <span className={styles.OpenButtonBody} />
            </button>
          )}
        </div>
      </div>
      <div className={`${styles.Body} ${bodyClassName}`} {...bodyAttributes}>
        {(() => {
          if (!hasBeenOpened.current) return null;
          if (!isRolledUp && !isContentReady && !disableDeferredRender) {
            return <Spinner className={styles.Loader} />;
          }

          return children;
        })()}
      </div>
    </div>
  );
};
